// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useCallback, useEffect, useState } from 'react'
import {
  Anchor,
  Box,
  Heading,
  Page,
  PageContent,
  PageHeader,
  Text
} from 'grommet'
import PropTypes from 'prop-types'
import { CircleInformation, Previous } from 'grommet-icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'

import { Layout } from '../../../commoncomponents/layout/Layout'
import Markdown from '../../../commoncomponents/Markdown'
import { useVisibilityContext } from '../../../context/visibility-context'
import {
  Button,
  DataTable,
  Loader,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  NoDataInfo,
  Typography
} from '../../../components'
import { get } from '../../../utils/api-utils'
import { displayApiError } from '../../../utils/error-handling-utils'
import { displayNotification } from '../../../utils/notificiation-utils'

import CreateScimIntegration from './components/CreateScimIntegrationModal'

const CreateScimWarningModal = ({ onClose }) => {
  const { t } = useTranslation(['iam', 'common'])
  return (
    <ModalDialog
      header={
        <ModalHeader
          title={
            <Box pad={{ bottom: 'small' }} width="medium">
              <Typography
                type="heading"
                level="2"
                testId="create-scim-integration-warning-title"
              >
                {t('external_scim_integrations.create_scim_integration_title')}
              </Typography>
            </Box>
          }
          subtitle={t(
            'external_scim_integrations.scim_integrations_limit_exceeded'
          )}
        />
      }
      footer={
        <ModalFooter
          right={
            <Button
              label={t('common:close')}
              onClick={() => onClose(false)}
              primary
              testId="close-button"
            />
          }
        />
      }
      testId="create-scim-integration-warning-modal"
    />
  )
}

CreateScimWarningModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

const ExternalScim = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['common', 'iam'])
  const [scimData, setScimData] = useState([])
  const [unClaimedDomins, setUnClaimedDomains] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [noDataLoader, setNodataLoader] = useState(false)
  const [showErrorNotification, setShowErrorNotification] = useState(null)
  const [createNewScim, setCreateNewScim] = useState(false)
  const [createNewScimWarningModal, setCreateNewScimWarningModal] =
    useState(false)

  const [successMessage, setSuccessMessage] = useState(null)

  // Permissions
  const { v2RbacPolicies } = useVisibilityContext()
  const v2ExternalScimReadPerm = v2RbacPolicies?.includes(
    'identity.scim-integration.read'
  )

  useEffect(() => {
    let isCurrent = true
    setNodataLoader(true)
    get('/internal-platform-tenant-ui/v2alpha2/domains')
      .then((response) => {
        if (isCurrent && response?.data?.items) {
          setUnClaimedDomains(response.data.items)
        }
      })
      .catch((error) => {
        setShowErrorNotification(
          displayApiError(error, t, setShowErrorNotification)
        )
      })
      .finally(() => setNodataLoader(false))

    return () => {
      isCurrent = false
    }
  }, [t])

  useEffect(() => {
    const message = location?.state?.notificationMessage ? (
      <Markdown>{location?.state?.notificationMessage}</Markdown>
    ) : null
    setSuccessMessage(message)
    if (location?.state?.notificationMessage) {
      navigate(location.pathname, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  const getScimPageData = useCallback(async () => {
    const nameQuery =
      searchQuery?.trim()?.length > 0
        ? `?filter=${encodeURIComponent(`domain eq ${searchQuery.trim()}`)}`
        : ''
    const scimUrl = `/internal-platform-tenant-ui/v2alpha2/scim-integrations${nameQuery}`
    try {
      const response = await get(scimUrl)
      if (response?.data?.items) {
        setScimData(response.data.items)
      }
    } catch (error) {
      setShowErrorNotification(
        displayApiError(error, t, setShowErrorNotification)
      )
    }
  }, [searchQuery, t])

  useEffect(() => {
    setLoading(true)
    if (v2ExternalScimReadPerm) {
      getScimPageData()
    }
    setLoading(false)
  }, [getScimPageData, searchQuery, t, v2ExternalScimReadPerm])

  const getCreateNewScimAction = () => {
    return scimData?.length === 10
      ? setCreateNewScimWarningModal(true)
      : setCreateNewScim(true)
  }

  const columns = [
    {
      property: 'name',
      header: t('iam:sso_profiles.column_name'),
      primary: true
    },
    {
      property: 'domains',
      header: t('iam:sso_profiles.column_domains'),
      render: (data) => {
        const domains = data?.domains
        const contentBox = domains || '--'
        return <Text>{contentBox}</Text>
      }
    },
    {
      property: 'age',
      type: 'numeric',
      header: (
        <Box justifyContent="space-between" flex>
          <Box alignContent="center" direction="row">
            <Heading margin="none" level="3">
              {t('iam:external_scim_integrations.credential_age')}
            </Heading>
            <Box alignSelf="center" pad={{ horizontal: 'xsmall' }}>
              {t('iam:external_scim_integrations.days')}
            </Box>
          </Box>
        </Box>
      ),
      render: (data) => {
        const Days = data?.age ?? '--'
        return <Typography type="text">{Days}</Typography>
      }
    }
  ]

  const handleSearchTerm = debounce((newSearchQuery) => {
    setSearchQuery(newSearchQuery)
  }, 500)

  const renderNoPermAndAddScimPage = () => {
    return (
      <NoDataInfo
        icon={<CircleInformation size="large" />}
        title={t(
          'iam:external_scim_integrations.no_external_SCIM_integrations_heading'
        )}
        gap="small"
        gapTitleSubtitle="small"
        action={
          v2RbacPolicies.includes('identity.scim-integration.create') &&
          scimData?.length === 0 && (
            <Button
              primary
              label={t('iam:external_scim_integrations.create_external_scim')}
              onClick={() => {
                setCreateNewScim(true)
              }}
              testId="no-data-create-external-scim-btn"
            />
          )
        }
      />
    )
  }

  const renderNoPermAndAddDomainPage = () => {
    return noDataLoader ? (
      <Box direction="row" align="center" justify="center">
        <Loader testId="no-permission-loader" />
      </Box>
    ) : (
      <NoDataInfo
        icon={<CircleInformation size="large" />}
        title={t(
          'iam:external_scim_integrations.no_external_SCIM_integrations_heading'
        )}
        subtitle={t(
          'iam:external_scim_integrations.no_external_SCIM_integrations_sub_heading'
        )}
        gap="small"
        gapTitleSubtitle="small"
        action={
          v2RbacPolicies?.includes('identity.domain-requests.create') &&
          scimData?.length === 0 && (
            <Button
              primary
              label={t('iam:domains.add_domain')}
              onClick={() => {
                navigate('/manage-account/organization/domains/add-domain')
              }}
              testId="no-data-add-domain-btn"
            />
          )
        }
      />
    )
  }

  const handleClickRow = (datum) => {
    navigate(`/manage-account/organization/external-scim/${datum.id}`)
  }

  const renderScimTable = () => {
    return (
      <Box width="large">
        <DataTable
          style={{ width: 'fit-content' }}
          summary={{
            entityName:
              scimData?.length === 1
                ? t('common:item_singular')
                : t('common:item_plural')
          }}
          grid={{
            columns,
            data: scimData,
            onClickRow: (e) => handleClickRow(e.datum)
          }}
          loading={loading}
          search={{
            onSearchValueChange: (value) => {
              handleSearchTerm(value)
            },
            placeholder: t('common:search'),
            width: 'xlarge'
          }}
          button={
            <Box align="end" width="xlarge">
              <Button
                secondary
                label={t('iam:external_scim_integrations.create_external_scim')}
                onClick={() => getCreateNewScimAction()}
                testId="create-external-scim-btn"
              />
            </Box>
          }
          testId="scim-integrations-datatable"
        />
      </Box>
    )
  }

  const selectRenderingScenario = () => {
    if (scimData?.length !== 0 || searchQuery?.length > 0 || loading) {
      return renderScimTable()
    }
    if (scimData?.length === 0 && unClaimedDomins?.length === 0 && !loading) {
      return renderNoPermAndAddDomainPage()
    }
    if (scimData?.length === 0 && unClaimedDomins?.length !== 0 && !loading) {
      return renderNoPermAndAddScimPage()
    }
    return null
  }

  const handleBackBtnClick = () => {
    navigate('/manage-account/organization')
  }

  return (
    <>
      {showErrorNotification}
      {successMessage &&
        displayNotification(successMessage, 'info', setSuccessMessage, '')}
      <Page data-testid="external-scim-integrations-page">
        <PageContent>
          <PageHeader
            parent={
              <Anchor
                label={t('iam:organization.governance_title')}
                icon={<Previous />}
                onClick={(event) => {
                  event.preventDefault()
                  handleBackBtnClick()
                }}
                margin={{ bottom: 'xsmall' }}
                data-testid="external-scim-integrations-back-btn"
              />
            }
            title={
              <Heading
                level="1"
                type="heading"
                margin="none"
                data-testid="external-scim-integrations-page-title"
              >
                {t('iam:external_scim_integrations.title')}
              </Heading>
            }
            subtitle={
              v2ExternalScimReadPerm && (
                <Box width="large">
                  <Typography
                    normalize
                    type="text"
                    size="large"
                    testId="external-scim-integrations-page-subtitle"
                  >
                    {t('iam:external_scim_integrations.subtitle')}
                  </Typography>
                </Box>
              )
            }
          />
          {v2ExternalScimReadPerm && !loading ? (
            selectRenderingScenario()
          ) : (
            <>
              {!loading && v2ExternalScimReadPerm === false ? (
                <NoDataInfo
                  icon={<CircleInformation size="large" />}
                  title={t('iam:igc_general.no_perm_title')}
                  titleHeadingLevel={2}
                  subtitle={t('iam:igc_general.no_perm_desc_scim')}
                />
              ) : (
                <Box direction="row" align="center" justify="center">
                  <Loader testId="no-permission-loader" />
                </Box>
              )}
            </>
          )}
        </PageContent>
      </Page>
      {createNewScim && (
        <CreateScimIntegration
          onSetOpen={setCreateNewScim}
          onSuccess={() => {
            getScimPageData()
          }}
        />
      )}
      {createNewScimWarningModal && (
        <CreateScimWarningModal onClose={setCreateNewScimWarningModal} />
      )}
    </>
  )
}

const ExternalScimPage = () => {
  return (
    <Layout>
      <ExternalScim align="start" justify="start" />
    </Layout>
  )
}

export default ExternalScimPage
